import { useQuery, useTeam } from '@vrplatform/data';
import { Loader, Button as MantineButton, Tooltip } from '@mantine/core';
import { whereConnectionStatusDefault } from '@vrplatform/ui-common';
import { useEffect } from 'react';
import { useAutomationHistoryDrawer } from '../hooks';

type Props = {
  type: 'reservations' | 'deposits' | 'payments';
};

export function useRunningSyncSubscription(type: Props['type']) {
  const [{ id: teamId }] = useTeam();

  const skip = !teamId;

  const queryData = useQuery(
    (q, args) => {
      if (!args.teamId) return null;

      return (
        q
          .syncs({
            limit: 1,
            order_by: [{ createdAt: 'desc' }],
            where: {
              status: { _in: ['started', 'queued'] },
              tenantId: { _eq: args.teamId },
              connection: {
                tenantId: { _eq: args.teamId },
                status: whereConnectionStatusDefault,
                app: {
                  category: {
                    _eq:
                      args.type === 'reservations'
                        ? 'propertyManagementSystem'
                        : undefined,
                    _in:
                      args.type !== 'reservations'
                        ? ['paymentGateway', 'bookingChannel']
                        : undefined,
                  },
                },
              },
            },
          })
          .map((x) => ({
            id: x.id,
            status: x.status,
            connection: {
              name: x.connection?.name || '-',
            },
          }))[0] ?? null
      );
    },
    {
      queryKey: ['tasks', 'automations', 'jobPlans'],
      skip,
      keepPreviousData: true,
      variables: {
        teamId,
        type,
      },
    }
  );

  const seconds = 3 * 5000;

  // refetch query every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!skip) {
        queryData.refetch();
      }
    }, seconds);

    return () => clearInterval(interval);
  }, [queryData.refetch, skip]);

  return queryData;
}

export const RunningSyncIndicator = ({ type }: Props) => {
  const { data } = useRunningSyncSubscription(type);
  const { open } = useAutomationHistoryDrawer();

  if (data) {
    return (
      <Tooltip
        withArrow
        label={
          data.connection.name
            ? `Syncing ${data.connection.name}...`
            : 'Syncing data...'
        }
      >
        <MantineButton
          variant="transparent"
          leftSection={<Loader size={14} color="gray" />}
          c="neutral"
          onClick={() =>
            open(data.id, {
              type: 'push',
              refreshQueryKeys: [type],
            })
          }
          size="xs"
          sx={{
            fontWeight: 400,
          }}
        >
          Syncing data...
        </MantineButton>
      </Tooltip>
    );
  }

  return null;
};
